import React, { useEffect, useRef, useState } from 'react';

import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { idempotencyHeader } from 'Helpers/apiUtils';

import Button from 'CommonComponents/Button';
import ModalHeader from 'CommonComponents/ModalHeader';

import { useToast } from 'Hooks/useToast';

const RequestSupplierSchema = Yup.object().shape({
    requested_name: Yup.string().required($t('This value is required')),
    contact_name: Yup.string().required($t('This value is required')),
    contact_email: Yup.string().email($t('Must be a valid email address')).required($t('This value is required')),
});

/**
 * Type-ahead "did you mean an existing supplier?" suggestions for the request modal (IB-610
 * stretch). Debounced lookup against the event's suppliers; active matches are one-click
 * selectable so the requestor can use an existing supplier instead of sending a duplicate
 * request, and ERP-inactive matches (dropped from the live sync list — IB-728) are surfaced as a
 * read-only warning so the requestor knows the supplier exists but needs reactivating.
 */
function SupplierSuggestions({ name, suggestionsUrl, onSelect }) {
    const [suggestions, setSuggestions] = useState([]);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!suggestionsUrl || !name || name.trim().length < 2) {
            setSuggestions([]);
            return undefined;
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            axios
                .get(suggestionsUrl, { params: { name } })
                .then(({ data }) => setSuggestions(data?.suggestions || []))
                .catch(() => setSuggestions([]));
        }, 350);

        return () => clearTimeout(timerRef.current);
    }, [name, suggestionsUrl]);

    if (suggestions.length === 0) {
        return null;
    }

    const active = suggestions.filter((s) => s.active);
    const inactive = suggestions.filter((s) => !s.active);

    return (
        <div className="tw-mt-2 tw-rounded-lg tw-border tw-border-grey tw-bg-grey/20 tw-p-2.5 tw-text-sm tw-space-y-1.5">
            <div className="tw-text-xs tw-font-semibold tw-text-charcoal/70">{$t('Already in your suppliers?')}</div>
            {active.map((supplier) => (
                <button
                    key={`a-${supplier.id}`}
                    type="button"
                    className="tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-py-1 tw-text-left hover:tw-bg-grey/40"
                    onClick={() => onSelect(supplier)}
                >
                    <span className="tw-font-medium tw-text-charcoal">{supplier.name}</span>
                    <span className="tw-text-xs tw-text-primary">{$t('Use this instead')}</span>
                </button>
            ))}
            {inactive.map((supplier) => (
                <div key={`i-${supplier.id}`} className="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-1 tw-text-amber-800">
                    <span className="tw-font-medium">{supplier.name}</span>
                    <span className="tw-text-xs">{$t('Exists but inactive in ERP')}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * "Request new supplier to be added" modal (IB-610).
 *
 * Sends the request email to the configured recipients AND saves the draft, then
 * surfaces the resulting SupplierRequest so the parent can show the awaiting banner.
 */
export default function RequestSupplierModal(props) {
    const toast = useToast();

    const recipients = props.recipients || [];

    const close = () => {
        props.modalElement && props.modalElement.hide();
    };

    // The requestor recognised an existing supplier from the suggestions: hand it to the parent to
    // select on the form (no request is sent) and close (IB-610 stretch).
    const handleSelectExisting = (supplier) => {
        props.onSelectExisting && props.onSelectExisting(supplier);
        toast.success($t('Using existing supplier :name', { name: supplier.name }));
        close();
    };

    const submit = (values, { setSubmitting }) => {
        // The request attaches to a draft document (UncommittedExpenditureGroup). Guard against a
        // missing requestable so we never POST a null requestable_id (422) — on EA the draft group
        // only exists once cost lines are selected (IB-610 finding #3 follow-up).
        if (!props.requestableId) {
            toast.error($t('Select at least one cost line before requesting a supplier.'));
            setSubmitting(false);
            return;
        }

        const sendRequest = () =>
            axios.post(
                props.storeUrl,
                {
                    requestable_type: props.requestableType,
                    requestable_id: props.requestableId,
                    requested_name: values.requested_name,
                    contact_name: values.contact_name,
                    contact_email: values.contact_email,
                },
                { headers: { ...idempotencyHeader() } },
            );

        // Persist the in-progress draft first so the "save draft" half of the button label is
        // truthful (IB-610). The parent supplies the current commitment metadata.
        //
        // The two-argument form of .then() is deliberate: the error branch only covers the
        // network calls (save draft + create request). The post-success UI step (rolling the
        // parent over to the awaiting state) is a SEPARATE .then, so a hiccup there can never
        // surface a false "error" toast for a request that actually persisted (IB-610 finding #3).
        Promise.resolve(props.onBeforeRequest ? props.onBeforeRequest() : null)
            .then(sendRequest)
            .then(
                ({ data }) => {
                    // The create response already carries the awaiting payload in the shape the
                    // parent renders from, so we update the store from it directly — no second fetch.
                    props.onRequested && props.onRequested(data);
                    toast.success($t('Supplier request sent'));
                    close();
                },
                () => {
                    toast.error($t('An error occurred, please try again'));
                },
            )
            .finally(() => {
                setSubmitting(false);
            });
    };

    return (
        <div id={props.modalId} ref={props.modalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
            <div className="modal__container">
                <div className="modal__inner-container">
                    <ModalHeader title={$t('Request new supplier to be added')} close={close}></ModalHeader>

                    <div className="tw-rounded-b">
                        <div className="modal__form-container">
                            <p className="tw-text-sm tw-text-charcoal/80 tw-mb-4">{$t('Your finance team will be emailed to add this supplier to the ERP.')}</p>

                            <Formik
                                initialValues={{ requested_name: props.initialName || '', contact_name: '', contact_email: '' }}
                                validationSchema={RequestSupplierSchema}
                                onSubmit={submit}
                            >
                                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                                    <form onSubmit={handleSubmit} className="tw-space-y-4">
                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                            <label htmlFor="requested_name" className="text-input__label">
                                                {$t('Supplier name')} <span className="tw-text-red tw-text-sm">*</span>
                                            </label>
                                            <input
                                                id="requested_name"
                                                name="requested_name"
                                                type="text"
                                                className="tw-block tw-w-full tw-rounded-lg tw-text-sm tw-border-grey"
                                                value={values.requested_name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {touched.requested_name && errors.requested_name && (
                                                <div className="tw-text-red tw-text-sm">{errors.requested_name}</div>
                                            )}
                                            {/* Surface existing suppliers that look similar so a duplicate isn't requested (IB-610). */}
                                            <SupplierSuggestions
                                                name={values.requested_name}
                                                suggestionsUrl={props.suggestionsUrl}
                                                onSelect={handleSelectExisting}
                                            />
                                        </div>

                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                            <label htmlFor="contact_name" className="text-input__label">
                                                {$t('Contact name')} <span className="tw-text-red tw-text-sm">*</span>
                                            </label>
                                            <input
                                                id="contact_name"
                                                name="contact_name"
                                                type="text"
                                                className="tw-block tw-w-full tw-rounded-lg tw-text-sm tw-border-grey"
                                                value={values.contact_name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {touched.contact_name && errors.contact_name && <div className="tw-text-red tw-text-sm">{errors.contact_name}</div>}
                                        </div>

                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                            <label htmlFor="contact_email" className="text-input__label">
                                                {$t('Contact email')} <span className="tw-text-red tw-text-sm">*</span>
                                            </label>
                                            <input
                                                id="contact_email"
                                                name="contact_email"
                                                type="email"
                                                className="tw-block tw-w-full tw-rounded-lg tw-text-sm tw-border-grey"
                                                value={values.contact_email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <p className="tw-text-xs tw-text-charcoal/70">
                                                {$t('Used by your AP team if they need to follow up with the supplier directly.')}
                                            </p>
                                            {touched.contact_email && errors.contact_email && (
                                                <div className="tw-text-red tw-text-sm">{errors.contact_email}</div>
                                            )}
                                        </div>

                                        <div className="tw-rounded-lg tw-bg-grey/40 tw-p-3 tw-text-sm tw-text-charcoal">
                                            {$t('This request will be sent to:')} <strong>{recipients.join(', ')}</strong>
                                            {' — '}
                                            {$t('Configured in Commitment Settings.')}
                                        </div>

                                        <div className="tw-flex tw-justify-end tw-gap-x-2 tw-pt-2">
                                            <Button type="button" variant="secondary" onClick={close} disabled={isSubmitting}>
                                                {$t('Cancel')}
                                            </Button>
                                            <Button type="submit" variant="primary" disabled={isSubmitting}>
                                                {$t('Send request & save draft')}
                                            </Button>
                                        </div>
                                    </form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
